import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';
import * as Popover from '@radix-ui/react-popover';
import AppIcon from './AppIcon';
import { cn } from '../utils/cn';
import {
  displayDateToIso,
  displayMonthToIso,
  isoDateToDisplay,
  isoMonthToDisplay,
  normalizeTime,
} from '../utils/appDateFormat';
import { formatLocalDateParam } from '../utils/apiDate';

/**
 * ── MACHINE-INDEPENDENT DATE / TIME FIELDS (owner decision 2026-09-17) ───────────────────────────
 *
 * Drop-in replacements for native `<input type="date|time|datetime-local|month">`. A native field
 * renders — and, when typed into, PARSES — in the viewer's machine locale: the same `17/09/2026`
 * is a valid day on a UK laptop and an empty value on a US one, which is how a work-point rule
 * silently failed to save. These fields always show `dd-mm-yyyy` / 24-hour `HH:mm` / `mm-yyyy`,
 * accept typing in exactly that shape, and speak ISO on the wire, so call sites keep their state:
 *
 *   DateField      value/onChange  'YYYY-MM-DD'         (was <input type="date">)
 *   TimeField      value/onChange  'HH:mm'              (was <input type="time">)
 *   DateTimeField  value/onChange  'YYYY-MM-DDTHH:mm'   (was <input type="datetime-local">)
 *   MonthField     value/onChange  'YYYY-MM'            (was <input type="month">)
 *
 * onChange fires with a complete, valid value, or '' when the field is cleared. A half-typed or
 * impossible value (31-02-2026) is held locally, flagged, and never emitted — the parent keeps its
 * last good value rather than receiving garbage.
 *
 * `data-testid` and `id` land on the text input, so Playwright uses `.fill('17-09-2026')`.
 */

/*
 * The text inputs are always `dir="ltr"` (dd-mm-yyyy, HH:mm read left to right in every language),
 * so the icon beside the text and the padding that makes room for it use PHYSICAL sides
 * (`right-0` + `pr-10`, `left-0` + `pl-9`). Logical ones resolve differently on the two elements in
 * Arabic: the input's `pe-*` follows its own LTR direction while the icon's `end-0` follows the RTL
 * wrapper, which drew the calendar icon over the first digits.
 */
const baseInputClasses =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors disabled:cursor-not-allowed disabled:opacity-50';

const dp = getDefaultClassNames();
const dayPickerClassNames = {
  root: `${dp.root} p-3`,
  months: `${dp.months} relative flex flex-col sm:flex-row gap-4`,
  month: `${dp.month} space-y-4`,
  month_caption: `${dp.month_caption} flex h-9 items-center justify-start`,
  caption_label: `${dp.caption_label} flex items-center gap-1 px-2 py-1 text-sm font-medium text-foreground`,
  dropdowns: `${dp.dropdowns} flex items-center justify-start gap-1.5`,
  dropdown_root: `${dp.dropdown_root} relative inline-flex items-center rounded-md border border-input bg-background hover:bg-muted transition-colors focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary`,
  dropdown: `${dp.dropdown} absolute inset-0 z-10 w-full cursor-pointer appearance-none opacity-0`,
  nav: `${dp.nav} absolute top-0 end-0 z-10 flex h-9 items-center gap-0.5`,
  button_previous: `${dp.button_previous} h-7 w-7 bg-transparent p-0 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors inline-flex items-center justify-center disabled:opacity-40`,
  button_next: `${dp.button_next} h-7 w-7 bg-transparent p-0 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors inline-flex items-center justify-center disabled:opacity-40`,
  month_grid: `${dp.month_grid} w-full border-collapse`,
  weekdays: `${dp.weekdays} flex`,
  weekday: `${dp.weekday} text-muted-foreground rounded-md w-9 font-normal text-xs`,
  week: `${dp.week} flex w-full mt-2`,
  day: `${dp.day} h-9 w-9 text-center text-sm p-0 relative`,
  day_button: `${dp.day_button} h-9 w-9 p-0 font-normal rounded-md hover:bg-muted transition-colors`,
  selected: `${dp.selected} bg-primary text-primary-foreground hover:bg-primary`,
  today: `${dp.today} bg-accent text-accent-foreground`,
  outside: `${dp.outside} text-muted-foreground opacity-50`,
  disabled: `${dp.disabled} text-muted-foreground opacity-50 cursor-not-allowed`,
};

/**
 * Insert separators while typing digits: `17092026` → `17-09-2026`, `1430` → `14:30`. Returns null for an edit
 * that would make the text longer than the full pattern, and the caller then keeps its current text: that is
 * what `maxLength` did, and these inputs carry no `maxLength` (CO-955). With `maxLength` set, Chromium blanked a
 * full field when a whole value was committed over a select-all through its input-method insert path (measured
 * with Playwright's fill(), which selects all and then commits the text that way; an IME commit takes the same
 * path): the replacement was cut to nothing and the field emptied, every other edit. Measured on dev 9c422b042:
 * 10 of 20 fills lost with the attribute, 4 of 4 kept once it was removed from the live input.
 */
const autoSeparate = (raw: string, groups: number[], sep: string): string | null => {
  if (raw.length > groups.reduce((a, g) => a + g, 0) + sep.length * (groups.length - 1)) return null;
  if (/[^\d]/.test(raw.replace(new RegExp(`\\${sep}`, 'g'), ''))) return raw; // user typed their own separators
  const digits = raw.replace(/\D/g, '');
  const parts: string[] = [];
  let i = 0;
  for (const g of groups) {
    if (i >= digits.length) break;
    parts.push(digits.slice(i, i + g));
    i += g;
  }
  return parts.join(sep);
};

const isoToLocalDate = (iso: string): Date | undefined => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : undefined;
};

interface BaseFieldProps {
  label?: React.ReactNode;
  description?: React.ReactNode;
  /** External error (server/parent validation). Takes precedence over the field's own format error. */
  error?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  'aria-label'?: string;
  'data-testid'?: string;
  title?: string;
  autoFocus?: boolean;
  onBlur?: () => void;
}

function FieldShell({
  inputId, label, required, error, description, className, children,
}: {
  inputId: string; label?: React.ReactNode; required?: boolean; error?: React.ReactNode;
  description?: React.ReactNode; className?: string; children: React.ReactNode;
}) {
  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label htmlFor={inputId} className={cn('text-sm font-medium leading-none', error ? 'text-destructive' : 'text-foreground')}>
          {label}
          {required && <span className="text-destructive ms-1">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p id={`${inputId}-error`} role="alert" className="text-sm text-destructive">{error}</p>
      ) : description ? (
        <p id={`${inputId}-description`} className="text-sm text-muted-foreground">{description}</p>
      ) : null}
    </div>
  );
}

/**
 * Shared engine: a text input holding the display text, emitting the wire value once it parses.
 * Re-syncs from `value` whenever the parent changes it to something this field did not emit.
 *
 * CO-955: a one-shot replace (clear + insert in the same task: Playwright `fill()`, some autofill) emits ''
 * and then the new value before the parent has re-rendered. The parent's render carrying the '' could land
 * after the second emission, and comparing it with only the LAST emission took it for an outside change and
 * blanked the field. So every emission the parent has not echoed yet is remembered, oldest first: a value
 * that is one of them is this field's own echo (an earlier one is stale and is skipped), never a reset.
 */
function useTypedField(value: string, toDisplay: (v: string) => string, onChange: (v: string) => void) {
  const [text, setText] = useState(() => toDisplay(value || ''));
  const pendingRef = useRef<string[]>([]);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    const incoming = value || '';
    const pending = pendingRef.current;
    const echoAt = pending.lastIndexOf(incoming);
    if (echoAt >= 0) {
      pendingRef.current = pending.slice(echoAt + 1);
      return;
    }
    pendingRef.current = [];
    setText(toDisplay(incoming));
    setTouched(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (wire: string) => {
    // Compared with what the parent will hold once it has caught up, not with the `value` of this render:
    // a clear then the old value again in one task must still send the old value back.
    const pending = pendingRef.current;
    const expected = pending.length > 0 ? pending[pending.length - 1] : (value || '');
    if (wire === expected) return;
    pendingRef.current = [...pending, wire];
    onChange(wire);
  };

  return { text, setText, emit, touched, setTouched };
}

// ── DateField ────────────────────────────────────────────────────────────────────────────────────

export interface DateFieldProps extends BaseFieldProps {
  /** 'YYYY-MM-DD' or ''. */
  value: string | null | undefined;
  onChange: (iso: string) => void;
  /** 'YYYY-MM-DD' bounds, inclusive. */
  min?: string;
  max?: string;
  /** 'YYYY-MM-DD' the calendar opens on while the field is empty; defaults to `min`. */
  defaultMonth?: string;
}

export const DateField: React.FC<DateFieldProps> = ({
  value, onChange, min, max, defaultMonth, label, description, error, required, disabled, id, name, className,
  inputClassName, placeholder, onBlur, ...rest
}) => {
  const { t } = useTranslation();
  const autoId = useId();
  const inputId = id || `date-${autoId}`;
  const [open, setOpen] = useState(false);
  const { text, setText, emit, touched, setTouched } = useTypedField(value || '', isoDateToDisplay, onChange);

  const iso = displayDateToIso(text);
  let ownError: string | null = null;
  if (text && !iso) ownError = t('Use the format dd-mm-yyyy');
  else if (iso && min && iso < min) ownError = t('Date must be on or after {{date}}', { date: isoDateToDisplay(min) });
  else if (iso && max && iso > max) ownError = t('Date must be on or before {{date}}', { date: isoDateToDisplay(max) });
  else if (!text && required && touched) ownError = t('This field is required');
  const shownError = error || (touched ? ownError : null);

  const handleText = (raw: string) => {
    const next = autoSeparate(raw, [2, 2, 4], '-');
    if (next === null) return;
    setText(next);
    if (!next) { emit(''); return; }
    const parsed = displayDateToIso(next);
    if (parsed && (!min || parsed >= min) && (!max || parsed <= max)) emit(parsed);
  };

  const selected = iso ? isoToLocalDate(iso) : undefined;
  const minDate = min ? isoToLocalDate(min) : undefined;
  const maxDate = max ? isoToLocalDate(max) : undefined;
  const describedBy = shownError ? `${inputId}-error` : description ? `${inputId}-description` : undefined;

  return (
    <FieldShell inputId={inputId} label={label} required={required} error={shownError} description={description} className={className}>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          id={inputId}
          name={name}
          value={text}
          placeholder={placeholder ?? 'dd-mm-yyyy'}
          disabled={disabled}
          aria-invalid={shownError ? true : undefined}
          aria-describedby={describedBy}
          required={required}
          aria-required={required || undefined}
          aria-label={rest['aria-label']}
          data-testid={rest['data-testid']}
          title={rest.title}
          autoFocus={rest.autoFocus}
          dir="ltr"
          className={cn(baseInputClasses, 'pr-10 text-start', shownError && 'border-destructive focus-visible:ring-destructive/50', inputClassName)}
          onChange={(e) => handleText(e.target.value)}
          onBlur={() => { setTouched(true); onBlur?.(); }}
        />
        <Popover.Root open={open} onOpenChange={(v) => { if (!disabled) setOpen(v); }}>
          <Popover.Trigger asChild disabled={disabled}>
            <button
              type="button"
              disabled={disabled}
              aria-label={t('Open calendar')}
              className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50"
            >
              <AppIcon name="Calendar" size={16} />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content className="z-50 rounded-md border border-border bg-card shadow-md" align="end" sideOffset={4}>
              <DayPicker
                mode="single"
                captionLayout="dropdown"
                fixedWeeks
                showOutsideDays
                selected={selected}
                defaultMonth={selected ?? (defaultMonth ? isoToLocalDate(defaultMonth) : minDate)}
                startMonth={minDate ?? new Date(new Date().getFullYear() - 100, 0, 1)}
                endMonth={maxDate ?? new Date(new Date().getFullYear() + 10, 11, 31)}
                disabled={minDate || maxDate ? [
                  ...(minDate ? [{ before: minDate }] : []),
                  ...(maxDate ? [{ after: maxDate }] : []),
                ] : undefined}
                onSelect={(day) => {
                  if (!day) return;
                  const picked = formatLocalDateParam(day);
                  setText(isoDateToDisplay(picked));
                  setTouched(true);
                  emit(picked);
                  setOpen(false);
                }}
                classNames={dayPickerClassNames}
              />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>
    </FieldShell>
  );
};

// ── TimeField ────────────────────────────────────────────────────────────────────────────────────

export interface TimeFieldProps extends BaseFieldProps {
  /** 'HH:mm' (24-hour) or ''. A trailing ':ss' on the incoming value is ignored. */
  value: string | null | undefined;
  onChange: (hhmm: string) => void;
}

const timeToDisplay = (v: string) => normalizeTime((v || '').slice(0, 5)) ?? '';

export const TimeField: React.FC<TimeFieldProps> = ({
  value, onChange, label, description, error, required, disabled, id, name, className,
  inputClassName, placeholder, onBlur, ...rest
}) => {
  const { t } = useTranslation();
  const autoId = useId();
  const inputId = id || `time-${autoId}`;
  const { text, setText, emit, touched, setTouched } = useTypedField(timeToDisplay(value || ''), timeToDisplay, onChange);

  const parsed = normalizeTime(text);
  let ownError: string | null = null;
  if (text && !parsed) ownError = t('timeField.formatHint');
  else if (!text && required && touched) ownError = t('This field is required');
  const shownError = error || (touched ? ownError : null);

  return (
    <FieldShell inputId={inputId} label={label} required={required} error={shownError} description={description} className={className}>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex w-9 items-center justify-center text-muted-foreground">
          <AppIcon name="Clock" size={16} />
        </span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          id={inputId}
          name={name}
          value={text}
          placeholder={placeholder ?? 'HH:mm'}
          disabled={disabled}
          aria-invalid={shownError ? true : undefined}
          aria-describedby={shownError ? `${inputId}-error` : undefined}
          required={required}
          aria-required={required || undefined}
          aria-label={rest['aria-label']}
          data-testid={rest['data-testid']}
          title={rest.title}
          autoFocus={rest.autoFocus}
          dir="ltr"
          className={cn(baseInputClasses, 'pl-9 text-start', shownError && 'border-destructive focus-visible:ring-destructive/50', inputClassName)}
          onChange={(e) => {
            const next = autoSeparate(e.target.value, [2, 2], ':');
            if (next === null) return;
            setText(next);
            if (!next) { emit(''); return; }
            const ok = normalizeTime(next);
            if (ok && next.length === 5) emit(ok);
          }}
          onBlur={() => {
            setTouched(true);
            const ok = normalizeTime(text);
            if (ok) { setText(ok); emit(ok); }
            onBlur?.();
          }}
        />
      </div>
    </FieldShell>
  );
};

// ── DateTimeField ────────────────────────────────────────────────────────────────────────────────

export interface DateTimeFieldProps extends Omit<BaseFieldProps, 'placeholder'> {
  /** 'YYYY-MM-DDTHH:mm' or ''. Seconds on the incoming value are ignored. */
  value: string | null | undefined;
  onChange: (value: string) => void;
  /** 'YYYY-MM-DD' bounds for the date part. */
  min?: string;
  max?: string;
  /** Time used when a date is picked before a time is entered. Default '00:00'. */
  defaultTime?: string;
}

export const DateTimeField: React.FC<DateTimeFieldProps> = ({
  value, onChange, min, max, defaultTime = '00:00', label, description, error, required, disabled, id, name,
  className, inputClassName, onBlur, ...rest
}) => {
  const { t } = useTranslation();
  const autoId = useId();
  const baseId = id || `datetime-${autoId}`;
  const datePart = value ? value.slice(0, 10) : '';
  const timePart = value && value.length >= 16 ? value.slice(11, 16) : '';
  const testId = rest['data-testid'];

  return (
    <FieldShell inputId={baseId} label={label} required={required} error={error} description={description} className={className}>
      <div className="grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-2">
        <DateField
          id={baseId}
          name={name}
          value={datePart}
          min={min?.slice(0, 10)}
          max={max?.slice(0, 10)}
          required={required}
          disabled={disabled}
          aria-label={rest['aria-label'] ? `${rest['aria-label']} — ${t('Date')}` : undefined}
          data-testid={testId ? `${testId}-date` : undefined}
          inputClassName={inputClassName}
          onBlur={onBlur}
          onChange={(d) => onChange(d ? `${d}T${timePart || defaultTime}` : '')}
        />
        <TimeField
          value={timePart}
          disabled={disabled || !datePart}
          aria-label={rest['aria-label'] ? `${rest['aria-label']} — ${t('Time')}` : t('Time')}
          data-testid={testId ? `${testId}-time` : undefined}
          inputClassName={inputClassName}
          onChange={(tm) => { if (datePart) onChange(`${datePart}T${tm || defaultTime}`); }}
        />
      </div>
    </FieldShell>
  );
};

// ── MonthField ───────────────────────────────────────────────────────────────────────────────────

export interface MonthFieldProps extends BaseFieldProps {
  /** 'YYYY-MM' or ''. */
  value: string | null | undefined;
  onChange: (yyyymm: string) => void;
  min?: string;
  max?: string;
}

export const MonthField: React.FC<MonthFieldProps> = ({
  value, onChange, min, max, label, description, error, required, disabled, id, name, className,
  inputClassName, placeholder, onBlur, ...rest
}) => {
  const { t } = useTranslation();
  const autoId = useId();
  const inputId = id || `month-${autoId}`;
  const { text, setText, emit, touched, setTouched } = useTypedField(value || '', isoMonthToDisplay, onChange);

  const iso = displayMonthToIso(text);
  let ownError: string | null = null;
  if (text && !iso) ownError = t('Use the format mm-yyyy');
  else if (iso && min && iso < min) ownError = t('Month must be on or after {{month}}', { month: isoMonthToDisplay(min) });
  else if (iso && max && iso > max) ownError = t('Month must be on or before {{month}}', { month: isoMonthToDisplay(max) });
  else if (!text && required && touched) ownError = t('This field is required');
  const shownError = error || (touched ? ownError : null);

  return (
    <FieldShell inputId={inputId} label={label} required={required} error={shownError} description={description} className={className}>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex w-9 items-center justify-center text-muted-foreground">
          <AppIcon name="Calendar" size={16} />
        </span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          id={inputId}
          name={name}
          value={text}
          placeholder={placeholder ?? 'mm-yyyy'}
          disabled={disabled}
          aria-invalid={shownError ? true : undefined}
          aria-describedby={shownError ? `${inputId}-error` : undefined}
          required={required}
          aria-required={required || undefined}
          aria-label={rest['aria-label']}
          data-testid={rest['data-testid']}
          title={rest.title}
          autoFocus={rest.autoFocus}
          dir="ltr"
          className={cn(baseInputClasses, 'pl-9 text-start', shownError && 'border-destructive focus-visible:ring-destructive/50', inputClassName)}
          onChange={(e) => {
            const next = autoSeparate(e.target.value, [2, 4], '-');
            if (next === null) return;
            setText(next);
            if (!next) { emit(''); return; }
            const parsed = displayMonthToIso(next);
            if (parsed && (!min || parsed >= min) && (!max || parsed <= max)) emit(parsed);
          }}
          onBlur={() => { setTouched(true); onBlur?.(); }}
        />
      </div>
    </FieldShell>
  );
};
